import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

// Attach token — checks both admin and member tokens
api.interceptors.request.use((config) => {
  const adminToken  = localStorage.getItem('jnaft_token');
  const memberToken = localStorage.getItem('jnaft_member_token');
  const token = adminToken || memberToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const path = window.location.pathname;

      // Never redirect away from auth pages — they handle their own state
      const isAuthPage = [
        '/member/login', '/member/register',
        '/member/forgot-password', '/member/reset-password',
        '/verify-email', '/admin/login',
      ].some(p => path.startsWith(p));

      if (!isAuthPage) {
        if (path.startsWith('/admin')) {
          localStorage.removeItem('jnaft_token');
          window.location.href = '/admin/login';
        } else if (path.startsWith('/member/dashboard')) {
          // Only redirect away from protected member pages
          localStorage.removeItem('jnaft_member_token');
          window.location.href = '/member/login';
        }
        // For all other pages (public, home, etc.) — do nothing, just reject
      }
    }
    return Promise.reject(err);
  }
);

// ── Admin Auth ────────────────────────────────────────────────
export const authAPI = {
  login:          (d)  => api.post('/auth/login', d),
  getMe:          ()   => api.get('/auth/me'),
  changePassword: (d)  => api.put('/auth/change-password', d),
  forgotPassword: (d)  => api.post('/auth/forgot-password', d),
  resetPassword:  (d)  => api.post('/auth/reset-password', d),
};

// ── Member Auth ───────────────────────────────────────────────
export const memberAuthAPI = {
  register:       (d)  => api.post('/member/register', d),
  verifyEmail:    (t)  => api.get(`/member/verify-email?token=${t}`),
  login:          (d)  => api.post('/member/login', d),
  getMe:          ()   => api.get('/member/me'),
  updateProfile:  (d)  => api.put('/member/profile', d),
  forgotPassword: (d)  => api.post('/member/forgot-password', d),
  resetPassword:  (d)  => api.post('/member/reset-password', d),
};

// ── Dhikr ─────────────────────────────────────────────────────
export const dhikrAPI = {
  getTypes:   ()   => api.get('/member/dhikr/types'),
  getToday:   ()   => api.get('/member/dhikr/today'),
  getHistory: ()   => api.get('/member/dhikr/history'),
  save:       (d)  => api.post('/member/dhikr/save', d),
};

// ── Admin Member Management ───────────────────────────────────
export const memberAdminAPI = {
  getAll:   (p)  => api.get('/admin/members', { params: p }),
  getStats: ()   => api.get('/admin/members/stats'),
  getOne:   (id) => api.get(`/admin/members/${id}`),
  approve:  (id) => api.post(`/admin/members/${id}/approve`),
  reject:   (id, d) => api.post(`/admin/members/${id}/reject`, d),
  delete:   (id) => api.delete(`/admin/members/${id}`),
};

// ── Member Directory (public) ─────────────────────────────────
export const directoryAPI = {
  getAll: (p) => api.get('/members/directory', { params: p }),
};

// ── Contact ───────────────────────────────────────────────────
export const contactAPI = {
  send:        (d)  => api.post('/contact', d),
  getMessages: (p)  => api.get('/admin/messages', { params: p }),
  markRead:    (id) => api.put(`/admin/messages/${id}/read`),
  markReplied: (id, d) => api.put(`/admin/messages/${id}/reply`, d),
  delete:      (id) => api.delete(`/admin/messages/${id}`),
};

// ── Admin Users ───────────────────────────────────────────────
export const usersAPI = {
  getAll:  ()        => api.get('/users'),
  create:  (d)       => api.post('/users', d),
  update:  (id, d)   => api.put(`/users/${id}`, d),
  delete:  (id)      => api.delete(`/users/${id}`),
};

// ── Events ────────────────────────────────────────────────────
export const eventsAPI = {
  getAll:  (p)     => api.get('/events', { params: p }),
  getOne:  (slug)  => api.get(`/events/${slug}`),
  create:  (d)     => api.post('/events', d),
  update:  (id, d) => api.put(`/events/${id}`, d),
  delete:  (id)    => api.delete(`/events/${id}`),
};

// ── Azkar ─────────────────────────────────────────────────────
export const azkarAPI = {
  getCategories:  (p)     => api.get('/azkar/categories', { params: p }),
  createCategory: (d)     => api.post('/azkar/categories', d),
  getAll:         (p)     => api.get('/azkar', { params: p }),
  getOne:         (id)    => api.get(`/azkar/${id}`),
  create:         (d)     => api.post('/azkar', d),
  update:         (id, d) => api.put(`/azkar/${id}`, d),
  delete:         (id)    => api.delete(`/azkar/${id}`),
};

// ── Quotes ────────────────────────────────────────────────────
export const quotesAPI = {
  getAll:  (p)     => api.get('/quotes', { params: p }),
  create:  (d)     => api.post('/quotes', d),
  update:  (id, d) => api.put(`/quotes/${id}`, d),
  delete:  (id)    => api.delete(`/quotes/${id}`),
};

// ── Gallery ───────────────────────────────────────────────────
export const galleryAPI = {
  getAlbums:   ()   => api.get('/gallery/albums'),
  createAlbum: (d)  => api.post('/gallery/albums', d),
  getItems:    (p)  => api.get('/gallery/items', { params: p }),
  uploadImage: (fd) => api.post('/gallery/items/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadAudio: (fd) => api.post('/gallery/items/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPDF:   (fd) => api.post('/gallery/items/pdf',   fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  addVideo:    (d)  => api.post('/gallery/items/video', d),
  deleteItem:  (id) => api.delete(`/gallery/items/${id}`),
};

// ── Sawia ─────────────────────────────────────────────────────
export const sawiaAPI = {
  getAll:  ()        => api.get('/sawia'),
  create:  (d)       => api.post('/sawia', d),
  update:  (id, d)   => api.put(`/sawia/${id}`, d),
  delete:  (id)      => api.delete(`/sawia/${id}`),
};

// ── Terrestrial Stations ─────────────────────────────────────────
export const terrestrialAPI = {
  getAll:  (p)     => api.get('/terrestrial-stations', { params: p }),
  create:  (d)     => api.post('/terrestrial-stations', d),
  update:  (id, d) => api.put(`/terrestrial-stations/${id}`, d),
  delete:  (id)    => api.delete(`/terrestrial-stations/${id}`),
};

// ── Translation proxy ─────────────────────────────────────────
export const translateAPI = {
  translate: (text, from='en', to='ar') => api.post('/translate', { text, from, to }),
};

// ── Radio ─────────────────────────────────────────────────────
export const radioAPI = {
  getConfig:    ()  => api.get('/radio'),
  updateConfig: (d) => api.put('/radio', d),
};

// ── Announcements ─────────────────────────────────────────────
export const announcementsAPI = {
  getAll:  ()      => api.get('/announcements'),
  create:  (d)     => api.post('/announcements', d),
  delete:  (id)    => api.delete(`/announcements/${id}`),
};

// ── Settings ──────────────────────────────────────────────────
export const settingsAPI = {
  getAll:  ()  => api.get('/settings'),
  update:  (d) => api.put('/settings', d),
};

// ── Library ───────────────────────────────────────────────────
export const libraryAPI = {
  getInfo:          ()        => api.get('/library/info'),
  updateInfo:       (d)       => api.put('/library/info', d),
  getSections:      ()        => api.get('/library/sections'),
  getSectionsAdmin: ()        => api.get('/admin/library/sections'),
  createSection:    (d)       => api.post('/library/sections', d),
  updateSection:    (id, d)   => api.put(`/library/sections/${id}`, d),
  deleteSection:    (id)      => api.delete(`/library/sections/${id}`),
};

// ── Islamiyah ─────────────────────────────────────────────────
export const islamiyahAPI = {
  getInfo:           ()       => api.get('/islamiyah/info'),
  updateInfo:        (d)      => api.put('/islamiyah/info', d),
  getBranches:       ()       => api.get('/islamiyah/branches'),
  getBranchesAdmin:  ()       => api.get('/admin/islamiyah/branches'),
  createBranch:      (d)      => api.post('/islamiyah/branches', d),
  updateBranch:      (id, d)  => api.put(`/islamiyah/branches/${id}`, d),
  deleteBranch:      (id)     => api.delete(`/islamiyah/branches/${id}`),
  // Teachers
  getTeachers:       (bid)    => api.get(`/islamiyah/branches/${bid}/teachers`),
  createTeacher:     (bid, d) => api.post(`/islamiyah/branches/${bid}/teachers`, d),
  updateTeacher:     (id, d)  => api.put(`/islamiyah/teachers/${id}`, d),
  deleteTeacher:     (id)     => api.delete(`/islamiyah/teachers/${id}`),
};

export default api;
